import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import i18n from "@/app/i18n";
import { useGetMeQuery } from "@/features/auth/api";
import { logout, setAuth, setSessionCheckDone } from "@/features/auth/slice";
import { Loader } from "@/shared/components";
import { runtimeConfig } from "@/shared/config/runtime";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { DEMO_CREATE_ROUTE, LOGIN_PAGE_ROUTE, clearFormDrafts } from "@/shared/utils";

const PRIVATE_NAMESPACES = [
  "Sidebar",
  "Pagination",
  "Notifications",
  "EmployeePortal",
  "ApplyApplicationPage",
  "EmployeesPage",
];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const isLoggingOut = useAppSelector((state) => state.auth.isLoggingOut);

  const hasTempToken = useAppSelector((state) => state.auth.tempToken !== null);
  const [isAuthResolved, setAuthResolved] = useState(false);

  const shouldSkip = isAuth || mustChangePassword || hasTempToken;

  useEffect(function preloadPrivateNamespaces() {
    i18n.loadNamespaces(PRIVATE_NAMESPACES).catch(function ignoreMissingNamespace() {});
  }, []);

  useEffect(() => {
    if (!isAuth && !mustChangePassword && !hasTempToken) {
      setAuthResolved(false);
      dispatch(setSessionCheckDone(false));
    }
  }, [isAuth, mustChangePassword, hasTempToken, dispatch]);

  const { data, isError, isLoading, isSuccess } = useGetMeQuery(undefined, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!shouldSkip && !isLoading) {
      dispatch(setSessionCheckDone(true));
    }
  }, [shouldSkip, isLoading, dispatch]);

  useEffect(() => {
    if (!shouldSkip && !isLoading) {
      if (isSuccess && data) {
        dispatch(setAuth(data));
        setAuthResolved(true);
      } else if (isError) {
        clearFormDrafts();
        dispatch(logout());
        setAuthResolved(true);
      }
    }
  }, [shouldSkip, isLoading, isSuccess, isError, data, dispatch]);

  useEffect(() => {
    if (isAuth) {
      setAuthResolved(true);
    }
  }, [isAuth]);

  if (isLoggingOut) {
    return <Loader isFullPage />;
  }

  if (!isAuthResolved || (!shouldSkip && isLoading)) {
    return <Loader isFullPage />;
  }

  if (!isAuth) {
    return <Navigate to={runtimeConfig.isDemo ? DEMO_CREATE_ROUTE : LOGIN_PAGE_ROUTE} replace />;
  }

  return children;
}

